/**
 * Theme preference: "system" (no data-theme, follows the OS), "dark" or "light".
 *
 * The source of truth is `<html data-theme>` (tokens.css keys off it), so any code that sets
 * the attribute — the header toggle or the /kit switcher — stays in sync through
 * `subscribeTheme`. The choice is remembered per browser in localStorage (best effort).
 */
import { useSyncExternalStore } from "react";

export type ThemePreference = "system" | "dark" | "light";

export const THEME_STORAGE_KEY = "relay.theme";

export const THEME_OPTIONS: readonly { value: ThemePreference; label: string }[] = [
  { value: "system", label: "System" },
  { value: "dark", label: "Dark" },
  { value: "light", label: "Light" },
];

export function parseTheme(raw: string | null | undefined): ThemePreference {
  return raw === "dark" || raw === "light" ? raw : "system";
}

function root(): HTMLElement | null {
  return typeof document === "undefined" ? null : document.documentElement;
}

export function getTheme(): ThemePreference {
  return parseTheme(root()?.dataset.theme);
}

export function readStoredTheme(storage: Pick<Storage, "getItem"> | null = safeStorage()) {
  try {
    return parseTheme(storage?.getItem(THEME_STORAGE_KEY));
  } catch {
    return "system";
  }
}

/** Sets `<html data-theme>` (removed for "system") and remembers the choice. */
export function setTheme(
  next: ThemePreference,
  storage: Pick<Storage, "setItem" | "removeItem"> | null = safeStorage(),
): void {
  const el = root();
  if (el) {
    if (next === "system") delete el.dataset.theme;
    else el.dataset.theme = next;
  }
  try {
    if (next === "system") storage?.removeItem(THEME_STORAGE_KEY);
    else storage?.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // Private mode / blocked storage: the theme still applies for this page view.
  }
}

/** Call once before the first render so the stored theme applies without a flash. */
export function applyStoredTheme(): void {
  const stored = readStoredTheme();
  if (stored !== "system") setTheme(stored, null);
}

export function subscribeTheme(onChange: () => void): () => void {
  const el = root();
  if (!el || typeof MutationObserver === "undefined") return () => {};
  const observer = new MutationObserver(onChange);
  observer.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
  return () => observer.disconnect();
}

/** Current preference, re-rendering whenever `<html data-theme>` changes. */
export function useTheme(): ThemePreference {
  return useSyncExternalStore(subscribeTheme, getTheme, () => "system");
}

function safeStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}
